import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Card, CardContent, Grid, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, CircularProgress, Alert, Select, MenuItem, Chip,
  TextField, IconButton, Tooltip, Button, Drawer, Link
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

// AU GST (BAS) — ACCRUAL basis. A faithful replica of BASReturnReport hitting the
// /api/accounting/bas-accrual/* endpoints. The BAS is already invoice-basis on both sides, so the
// figures match the cash BAS tab; the difference here is that input GST reads the SCM-owned,
// editable supplier_invoices table (subsidiary Life O Pty Ltd) instead of quickbooks.* directly.
const API_URL = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';
const BASE = `${API_URL}/api/accounting/bas-accrual`;

function fmt(n) {
  return new Intl.NumberFormat('en', { style: 'currency', currency: 'AUD' }).format(Number(n || 0));
}

function basQuarters() {
  const startMonthToQ = { 7: 1, 10: 2, 1: 3, 4: 4 };
  const monthNames = { 1: 'Jan–Mar', 4: 'Apr–Jun', 7: 'Jul–Sep', 10: 'Oct–Dec' };
  const today = new Date().toISOString().slice(0, 10);
  const thisYear = new Date().getFullYear();
  const out = [];
  for (let y = thisYear + 1; y >= 2024; y--) {
    for (const sm of [10, 7, 4, 1]) {
      const endMonth = sm + 2;
      const from = `${y}-${String(sm).padStart(2, '0')}-01`;
      const lastDay = new Date(y, endMonth, 0).getDate();
      const to = `${y}-${String(endMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      if (from > today) continue;
      const fy = sm >= 7 ? y + 1 : y;
      const label = `${monthNames[sm]} ${y} · Q${startMonthToQ[sm]} FY${String(fy).slice(2)}`;
      out.push({ value: `${from}_${to}`, label, from, to });
    }
  }
  return out;
}

const QUARTERS = basQuarters();
const DEFAULT_PERIOD = QUARTERS.length ? QUARTERS[0].value : 'custom';

const DRILLDOWN_CONFIGS = {
  'shopify-sales': {
    title: 'Shopify AU sales — per order',
    endpoint: '/details/shopify-sales',
    note: 'One row per AU (AUD) Shopify order with GST > 0. Sum of `gst` reconciles to the 1A breakdown line.',
    columns: [
      { key: 'event_date', label: 'Date' },
      { key: 'brand_db', label: 'Brand DB' },
      { key: 'order_name', label: 'Order' },
      { key: 'currency', label: 'Cur' },
      { key: 'gross', label: 'Gross', align: 'right', money: true },
      { key: 'net', label: 'Net', align: 'right', money: true },
      { key: 'gst', label: 'GST', align: 'right', money: true, bold: true },
    ],
    sumKey: 'gst',
  },
  'shopify-refunds': {
    title: 'Shopify AU refunds — per refund',
    endpoint: '/details/shopify-refunds',
    note: 'Refund GST computed from the parent order rate.',
    columns: [
      { key: 'refund_date', label: 'Refund Date' },
      { key: 'brand_db', label: 'Brand DB' },
      { key: 'order_id', label: 'Order ID' },
      { key: 'refund_amt', label: 'Refund Amt', align: 'right', money: true },
      { key: 'computed_refund_gst', label: 'Computed GST', align: 'right', money: true, bold: true },
    ],
    sumKey: 'computed_refund_gst',
  },
  'amazon-au': {
    title: 'Amazon.com.au — per financial event',
    endpoint: '/details/amazon-au',
    note: 'One row per Amazon.com.au financial event. GST is SELF-ASSESSED = 1/11 of the GST-inclusive net (Principal + Shipping + Gift Wrap − Promotions): Amazon does NOT collect/remit GST on these domestic AU sales, so the ABN-registered seller self-assesses it. "Seller Tax" / "Ship Tax" are what Amazon actually collected (≈ A$0) shown for reference only.',
    columns: [
      { key: 'posted_date', label: 'Posted' },
      { key: 'amazon_order_id', label: 'Order ID' },
      { key: 'event_name', label: 'Event' },
      { key: 'currency', label: 'Cur' },
      { key: 'principal', label: 'Principal', align: 'right', money: true },
      { key: 'promotion', label: 'Promo', align: 'right', money: true },
      { key: 'tax', label: 'Seller Tax', align: 'right', money: true },
      { key: 'shipping_tax', label: 'Ship Tax', align: 'right', money: true },
      { key: 'gst_total', label: 'GST (self-assessed)', align: 'right', money: true, bold: true },
    ],
    sumKey: 'gst_total',
  },
  'sales-all': {
    title: 'G1 / 1A — all sales transactions',
    endpoint: '/details/sales-all',
    note: 'Every sales-side transaction across Shopify AU, refunds, Amazon.com.au and wholesale. Sum of GST = 1A within rounding pennies.',
    columns: [
      { key: 'date', label: 'Date' },
      { key: 'component', label: 'Component' },
      { key: 'reference', label: 'Order / Ref' },
      { key: 'detail', label: 'Detail' },
      { key: 'currency', label: 'Cur' },
      { key: 'gross', label: 'Gross', align: 'right', money: true },
      { key: 'gst', label: 'GST', align: 'right', money: true, bold: true },
    ],
    sumKey: 'gst',
  },
  'net-all': {
    title: 'Net GST — all transactions, signed',
    endpoint: '/details/net-all',
    note: 'Sales GST positive, supplier-invoice GST negative — the sum is the net GST payable (7A) within rounding pennies.',
    columns: [
      { key: 'date', label: 'Date' },
      { key: 'component', label: 'Component' },
      { key: 'reference', label: 'Reference' },
      { key: 'detail', label: 'Detail' },
      { key: 'currency', label: 'Cur' },
      { key: 'gst', label: 'GST (signed)', align: 'right', money: true, bold: true },
    ],
    sumKey: 'gst',
  },
  'wholesale-au': {
    title: 'Wholesale AU sales — per order',
    endpoint: '/details/wholesale-au',
    note: 'One row per recognized AU wholesale order (retail_orders), stored GST at the tax point; rows with GST 0.00 are not declared.',
    columns: [
      { key: 'tax_point_date', label: 'Tax Point' },
      { key: 'brand_db', label: 'Brand DB' },
      { key: 'source', label: 'Source' },
      { key: 'order_name', label: 'Order' },
      { key: 'customer_name', label: 'Customer' },
      { key: 'currency', label: 'Cur' },
      { key: 'net', label: 'Net', align: 'right', money: true },
      { key: 'tax', label: 'GST', align: 'right', money: true, bold: true },
    ],
    sumKey: 'tax',
  },
  'supplier-bills': {
    title: 'AU supplier invoices (SCM) — per bill, at invoice date',
    endpoint: '/details/supplier-bills',
    note: 'AU supplier invoices from the SCM-owned supplier_invoices table (imported from QuickBooks, editable in SCM) — NOT read from quickbooks.* directly. GST = recorded tax, FX-converted to AUD. Sum of `input_gst` reconciles to 1B.',
    columns: [
      { key: 'txn_date', label: 'Date' },
      { key: 'vendor', label: 'Vendor' },
      { key: 'doc_number', label: 'Doc' },
      { key: 'currency', label: 'Cur' },
      { key: 'exchange_rate', label: 'FX→AUD', align: 'right' },
      { key: 'total_amount', label: 'Total (AUD)', align: 'right', money: true },
      { key: 'input_gst', label: 'Input GST', align: 'right', money: true, bold: true },
      { key: 'status', label: 'Status' },
    ],
    sumKey: 'input_gst',
  },
  'amazon-fees': {
    title: 'Amazon.com.au fees — per month, by fee type',
    endpoint: '/details/amazon-fees',
    note: 'Amazon.com.au per-order seller fees: FBA fulfilment, referral (commission), refund-commission reversals, shipping chargebacks. The GST-inclusive amount feeds G11; input GST = amount/11 feeds 1B. Excludes account-level FBA subscription/storage (pending a de-dup fix) and Amazon Ads (separate).',
    columns: [
      { key: 'period_month', label: 'Month' },
      { key: 'fee_label', label: 'Fee type' },
      { key: 'gross_fees_incl', label: 'Fee (incl GST)', align: 'right', money: true },
      { key: 'input_gst', label: 'Input GST', align: 'right', money: true, bold: true },
      { key: 'row_count', label: 'Lines', align: 'right' },
    ],
    sumKey: 'input_gst',
  },
};

function fmtCell(v, money) {
  if (v === null || v === undefined || v === '') return '';
  if (money) return fmt(v);
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return v.slice(0, 10);
  return String(v);
}

export default function BASAccrualReport() {
  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const _init = QUARTERS[0] || { from: '', to: '' };
  const [customFrom, setCustomFrom] = useState(_init.from);
  const [customTo, setCustomTo] = useState(_init.to);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [drill, setDrill] = useState(null);

  const token = () => localStorage.getItem('token');

  const getRange = () => {
    if (period === 'custom') return { from: customFrom, to: customTo };
    const [from, to] = period.split('_');
    return { from, to };
  };
  const periodLabel = period === 'custom'
    ? `${customFrom} → ${customTo}`
    : (QUARTERS.find(q => q.value === period)?.label || period);

  const openDrill = async (configKey) => {
    const cfg = DRILLDOWN_CONFIGS[configKey];
    if (!cfg) return;
    const { from, to } = getRange();
    setDrill({ configKey, rows: null, loading: true, error: null });
    try {
      const res = await axios.get(`${BASE}${cfg.endpoint}?from=${from}&to=${to}&limit=2000`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (res.data.success) setDrill({ configKey, rows: res.data.data || [], loading: false, error: null });
      else setDrill({ configKey, rows: [], loading: false, error: res.data.error || 'Failed to load' });
    } catch (e) {
      setDrill({ configKey, rows: [], loading: false, error: e.response?.data?.error || e.message });
    }
  };
  const closeDrill = () => setDrill(null);

  const downloadExcel = async () => {
    const { from, to } = getRange();
    setDownloading(true);
    try {
      const res = await axios.get(`${BASE}/export?from=${from}&to=${to}`, {
        headers: { Authorization: `Bearer ${token()}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bas-accrual-${from}_to_${to}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (e) {
      alert(`Excel export failed: ${e.response?.data?.error || e.message}`);
    } finally {
      setDownloading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    const { from, to } = getRange();
    try {
      const res = await axios.get(`${BASE}/summary?from=${from}&to=${to}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (res.data.success) setSummary(res.data.data);
      else setError(res.data.error);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    if (period === 'custom' && (!customFrom || !customTo)) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const agg = (summary || []).reduce((a, m) => ({
    box1a: (a.box1a || 0) + Number(m.box1a_output_gst),
    box1b: (a.box1b || 0) + Number(m.box1b_input_gst),
    g1: (a.g1 || 0) + Number(m.g1_total_sales),
    g11: (a.g11 || 0) + Number(m.g11_total_purchases),
    shopify_output: (a.shopify_output || 0) + Number(m.shopify_output_gst),
    shopify_refunds: (a.shopify_refunds || 0) + Number(m.shopify_refund_gst),
    amazon_output: (a.amazon_output || 0) + Number(m.amazon_au_shipment_gst) + Number(m.amazon_au_refund_gst),
    wholesale_output: (a.wholesale_output || 0) + Number(m.wholesale_output_gst || 0),
    wholesale_zero_net: (a.wholesale_zero_net || 0) + Number(m.wholesale_zero_gst_net || 0),
    supplier_input: (a.supplier_input || 0) + Number(m.supplier_input_gst),
    amazon_fee_input: (a.amazon_fee_input || 0) + Number(m.amazon_au_fee_input_gst || 0),
    amazon_fee_expenses: (a.amazon_fee_expenses || 0) + Number(m.amazon_au_fee_expenses_incl || 0),
  }), {});

  const netGst = (agg.box1a || 0) - (agg.box1b || 0);

  if (loading) return (<Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>);
  if (error) return (<Alert severity="error" sx={{ m: 2 }}>{error}</Alert>);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ mr: 1 }}>AU BAS Return — Accrual (Preview)</Typography>
        <Chip size="small" color="primary" variant="outlined" label="Invoice basis" />
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" size="small"
          startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
          onClick={downloadExcel} disabled={downloading}>
          {downloading ? 'Building…' : 'Download Excel'}
        </Button>
        <Select value={period} onChange={(e) => setPeriod(e.target.value)} size="small" sx={{ minWidth: 200 }}>
          {QUARTERS.map(q => <MenuItem key={q.value} value={q.value}>{q.label}</MenuItem>)}
          <MenuItem value="custom">Custom range…</MenuItem>
        </Select>
        {period === 'custom' && (
          <>
            <TextField type="date" size="small" label="From" InputLabelProps={{ shrink: true }}
              value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} sx={{ width: 160 }} />
            <TextField type="date" size="small" label="To" InputLabelProps={{ shrink: true }}
              value={customTo} onChange={(e) => setCustomTo(e.target.value)} sx={{ width: 160 }} />
            <Button variant="contained" size="small" onClick={load} disabled={!customFrom || !customTo}>Apply</Button>
          </>
        )}
        <Tooltip title="Refresh"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Accrual (invoice) basis.</strong> AU GST computed from source data in AUD. Input GST (1B) comes from the
        <strong> SCM-owned supplier invoices</strong> (imported from QuickBooks, editable in SCM) at invoice date — not from
        quickbooks.* directly. The BAS is already invoice-basis, so these figures match the cash BAS tab.
        <strong> Preview only</strong> — not lodged with the ATO.
      </Alert>

      {/* BAS GST LABELS */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>BAS GST labels for {periodLabel}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Click a label row to see all its underlying transactions.</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.50' }}>
                  <TableCell>Label</TableCell><TableCell>Description</TableCell><TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow hover onClick={() => openDrill('sales-all')} sx={{ cursor: 'pointer' }}><TableCell>G1</TableCell><TableCell>Total sales (including GST)</TableCell><TableCell align="right">{fmt(agg.g1)}</TableCell></TableRow>
                <TableRow hover onClick={() => openDrill('sales-all')} sx={{ bgcolor: 'grey.50', cursor: 'pointer' }}><TableCell>1A</TableCell><TableCell>GST on sales</TableCell><TableCell align="right"><strong>{fmt(agg.box1a)}</strong></TableCell></TableRow>
                <TableRow hover onClick={() => openDrill('supplier-bills')} sx={{ cursor: 'pointer' }}><TableCell>G11</TableCell><TableCell>Non-capital purchases (including GST)</TableCell><TableCell align="right">{fmt(agg.g11)}</TableCell></TableRow>
                <TableRow hover onClick={() => openDrill('supplier-bills')} sx={{ bgcolor: 'grey.50', cursor: 'pointer' }}><TableCell>1B</TableCell><TableCell>GST on purchases</TableCell><TableCell align="right"><strong>{fmt(agg.box1b)}</strong></TableCell></TableRow>
                <TableRow hover onClick={() => openDrill('net-all')} sx={{ bgcolor: netGst >= 0 ? 'error.50' : 'success.50', cursor: 'pointer' }}>
                  <TableCell>{netGst >= 0 ? '7A' : '7B'}</TableCell>
                  <TableCell>Net GST {netGst >= 0 ? 'payable to ATO' : 'refund from ATO'} (1A − 1B)</TableCell>
                  <TableCell align="right"><strong>{fmt(Math.abs(netGst))}</strong></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* BREAKDOWN */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>GST on sales (1A) breakdown</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Click any row to see the underlying transactions.</Typography>
              <Table size="small">
                <TableBody>
                  <TableRow hover onClick={() => openDrill('shopify-sales')} sx={{ cursor: 'pointer' }}>
                    <TableCell>Shopify AU sales</TableCell><TableCell align="right">{fmt(agg.shopify_output)}</TableCell>
                  </TableRow>
                  <TableRow hover onClick={() => openDrill('shopify-refunds')} sx={{ cursor: 'pointer' }}>
                    <TableCell>Shopify AU refunds</TableCell><TableCell align="right" sx={{ color: 'error.main' }}>-{fmt(agg.shopify_refunds)}</TableCell>
                  </TableRow>
                  <TableRow hover onClick={() => openDrill('amazon-au')} sx={{ cursor: 'pointer' }}>
                    <TableCell>Amazon.com.au <Typography variant="caption" color="text.secondary">(self-assessed 1/11)</Typography></TableCell>
                    <TableCell align="right">{fmt(agg.amazon_output)}</TableCell>
                  </TableRow>
                  <TableRow hover onClick={() => openDrill('wholesale-au')} sx={{ cursor: 'pointer' }}>
                    <TableCell>Wholesale AU sales</TableCell><TableCell align="right">{fmt(agg.wholesale_output)}</TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: 'grey.50' }}><TableCell><strong>Total 1A</strong></TableCell><TableCell align="right"><strong>{fmt(agg.box1a)}</strong></TableCell></TableRow>
                  <TableRow hover onClick={() => openDrill('wholesale-au')} sx={{ cursor: 'pointer' }}>
                    <TableCell sx={{ color: (agg.wholesale_zero_net || 0) > 10000 ? 'warning.main' : 'text.secondary' }}>Wholesale AU rows with no GST recorded (net — A$0 declared)</TableCell>
                    <TableCell align="right" sx={{ color: (agg.wholesale_zero_net || 0) > 10000 ? 'warning.main' : 'text.secondary' }}>{fmt(agg.wholesale_zero_net)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>GST on purchases (1B) breakdown</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Click any row to see the underlying transactions.</Typography>
              <Table size="small">
                <TableBody>
                  <TableRow hover onClick={() => openDrill('supplier-bills')} sx={{ cursor: 'pointer' }}>
                    <TableCell>AU supplier invoices (SCM)</TableCell>
                    <TableCell align="right">{fmt(agg.supplier_input)}</TableCell>
                  </TableRow>
                  <TableRow hover onClick={() => openDrill('amazon-fees')} sx={{ cursor: 'pointer' }}>
                    <TableCell>Amazon.com.au fees <Typography variant="caption" color="text.secondary">(per-order: FBA + referral + chargebacks; excl. subscription/storage — pending de-dup)</Typography></TableCell>
                    <TableCell align="right">{fmt(agg.amazon_fee_input)}</TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: 'grey.50' }}><TableCell><strong>Total 1B</strong></TableCell><TableCell align="right"><strong>{fmt(agg.box1b)}</strong></TableCell></TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* PER-MONTH TABLE */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Per-month detail</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.50' }}>
                  <TableCell>Month</TableCell>
                  <TableCell align="right">1A — GST on sales</TableCell>
                  <TableCell align="right">1B — GST on purchases</TableCell>
                  <TableCell align="right">Net GST</TableCell>
                  <TableCell align="right">G1 — Total sales</TableCell>
                  <TableCell align="right">G11 — Total purchases</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(summary || []).map(m => (
                  <TableRow key={m.period_month} hover>
                    <TableCell>{m.period_month}</TableCell>
                    <TableCell align="right">{fmt(m.box1a_output_gst)}</TableCell>
                    <TableCell align="right">{fmt(m.box1b_input_gst)}</TableCell>
                    <TableCell align="right"><strong>{fmt(m.net_gst)}</strong></TableCell>
                    <TableCell align="right">{fmt(m.g1_total_sales)}</TableCell>
                    <TableCell align="right">{fmt(m.g11_total_purchases)}</TableCell>
                  </TableRow>
                ))}
                {(summary || []).length === 0 && (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}>No data in this period.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* DRILL-DOWN DRAWER */}
      <Drawer anchor="right" open={!!drill} onClose={closeDrill} PaperProps={{ sx: { width: { xs: '100%', md: '70%' }, maxWidth: 1200 } }}>
        {drill && (() => {
          const cfg = DRILLDOWN_CONFIGS[drill.configKey];
          if (!cfg) return null;
          const total = cfg.sumKey && drill.rows
            ? drill.rows.reduce((s, r) => s + Number(r[cfg.sumKey] || 0), 0)
            : null;
          return (
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>{cfg.title}</Typography>
                <Tooltip title="Close"><IconButton onClick={closeDrill}><CloseIcon /></IconButton></Tooltip>
              </Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                {cfg.note} {drill.rows && (
                  <Box component="span" sx={{ display: 'block', mt: 1 }}>
                    Showing <strong>{drill.rows.length}</strong> rows for {periodLabel}
                    {total !== null && (<> &nbsp;·&nbsp; Sum of {cfg.sumKey}: <strong>{fmt(total)}</strong></>)}
                    . For the full list, use <Link component="button" type="button" onClick={downloadExcel} sx={{ verticalAlign: 'baseline' }}>Download Excel <OpenInNewIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /></Link>.
                  </Box>
                )}
              </Alert>
              {drill.loading && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>}
              {drill.error && <Alert severity="error">{drill.error}</Alert>}
              {!drill.loading && !drill.error && drill.rows && (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(100vh - 220px)' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {cfg.columns.map(c => (
                          <TableCell key={c.key} align={c.align || 'left'} sx={{ bgcolor: 'primary.50', fontWeight: 600 }}>{c.label}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {drill.rows.length === 0 && (
                        <TableRow><TableCell colSpan={cfg.columns.length} align="center" sx={{ py: 4 }}>No transactions in this period.</TableCell></TableRow>
                      )}
                      {drill.rows.map((r, i) => (
                        <TableRow key={i} hover>
                          {cfg.columns.map(c => (
                            <TableCell key={c.key} align={c.align || 'left'} sx={c.bold ? { fontWeight: 600 } : undefined}>
                              {fmtCell(r[c.key], c.money)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          );
        })()}
      </Drawer>
    </Box>
  );
}
